// src/features/executive/performance/widgets/PatientSummaryCard.tsx
import { Box, Divider, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { useHero } from 'features/executive/components/WidgetCard/HeroContext'
import { usePatientSummary } from '../hooks/use-patient-summary'

const PREFIX = 'EXECUTIVE.PERFORMANCE.PATIENT_SUMMARY'
const fmt = (n: number) => n.toLocaleString('th-TH')

function Delta({ current, previous, small }: { current: number; previous: number; small?: boolean }) {
  const { onHero } = useHero()
  const diff = current - previous
  const arrow = diff > 0 ? '▲' : diff < 0 ? '▼' : '±'
  const sign = diff > 0 ? '+' : ''
  // On hero we drop the green/red split for white-on-color legibility; arrows
  // and signed numbers carry the direction signal on their own.
  const color = onHero
    ? 'rgba(255,255,255,0.85)'
    : diff > 0
    ? 'success.main'
    : diff < 0
    ? 'error.main'
    : 'text.disabled'
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        lineHeight: 1.2,
        fontSize: small ? '0.66rem' : '0.72rem',
        whiteSpace: 'nowrap',
        color,
      }}
    >
      {arrow} {sign}
      {fmt(diff)}
    </Typography>
  )
}

function StatCell({
  value,
  prev,
  unit,
  big,
}: {
  value: number
  prev: number
  unit: string
  big?: boolean
}) {
  const { onHero } = useHero()
  return (
    <Box sx={{ minWidth: 78, textAlign: 'right' }}>
      <Box>
        <Typography
          component="span"
          sx={{
            fontWeight: 700,
            fontSize: big ? '1.15rem' : '0.95rem',
            lineHeight: 1.3,
            color: onHero ? '#FFFFFF' : undefined,
          }}
        >
          {fmt(value)}
        </Typography>{' '}
        <Typography
          component="span"
          variant="caption"
          sx={{ color: onHero ? 'rgba(255,255,255,0.85)' : 'text.secondary' }}
        >
          {unit}
        </Typography>
      </Box>
      <Delta current={value} previous={prev} small={!big} />
    </Box>
  )
}

interface RowProps {
  label: string
  patients: number
  prevPatients: number
  visits: number
  prevVisits: number
  unitP: string
  unitV: string
  emphasis?: boolean
}

function StatRow({
  label,
  patients,
  prevPatients,
  visits,
  prevVisits,
  unitP,
  unitV,
  emphasis,
}: RowProps) {
  const { onHero } = useHero()
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          fontWeight: emphasis ? 600 : 400,
          pt: emphasis ? 0.5 : 0.25,
          color: onHero
            ? emphasis
              ? '#FFFFFF'
              : 'rgba(255,255,255,0.85)'
            : emphasis
            ? 'text.primary'
            : 'text.secondary',
        }}
      >
        {label}
      </Typography>
      <StatCell value={patients} prev={prevPatients} unit={unitP} big={emphasis} />
      <StatCell value={visits} prev={prevVisits} unit={unitV} big={emphasis} />
    </Box>
  )
}

export default function PatientSummaryCard() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = usePatientSummary()

  const isEmpty = !data || data.totalPatients === 0
  const unitP = t(`${PREFIX}.UNIT_PATIENTS`, 'คน')
  const unitV = t(`${PREFIX}.UNIT_VISITS`, 'ครั้ง')
  const p = data?.previous

  const tooltip = p ? [
    t(`${PREFIX}.TOOLTIP`),
    '',
    `${t(`${PREFIX}.PREV_PERIOD`)}:`,
    `• ${t(`${PREFIX}.ALL`)}: ${fmt(p.totalPatients)} ${unitP} / ${fmt(p.totalVisits)} ${unitV}`,
    `• ${t(`${PREFIX}.NEW`)}: ${fmt(p.newPatients)} ${unitP} / ${fmt(p.newVisits)} ${unitV}`,
    `• ${t(`${PREFIX}.EXISTING`)}: ${fmt(p.existingPatients)} ${unitP} / ${fmt(p.existingVisits)} ${unitV}`,
  ].join('\n') : (t(`${PREFIX}.TOOLTIP`) as string)

  return (
    <WidgetCard
      title={t(`${PREFIX}.TITLE`, 'สรุปคนไข้')}
      titleTooltip={tooltip}
      loading={loading}
      error={error}
      empty={isEmpty}
      onRetry={refetch}
      skeletonHeight={140}
      footer={
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', fontSize: '0.68rem' }}
        >
          {t(`${PREFIX}.VS_PREV`, '▲▼ เทียบช่วงเวลาก่อนหน้า')}
        </Typography>
      }
    >
      {data && p && (
        <Box>
          <StatRow
            label={t(`${PREFIX}.ALL`, 'คนไข้ทั้งหมด')}
            patients={data.totalPatients}
            prevPatients={p.totalPatients}
            visits={data.totalVisits}
            prevVisits={p.totalVisits}
            unitP={unitP}
            unitV={unitV}
            emphasis
          />
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <StatRow
              label={t(`${PREFIX}.NEW`, 'คนไข้ใหม่')}
              patients={data.newPatients}
              prevPatients={p.newPatients}
              visits={data.newVisits}
              prevVisits={p.newVisits}
              unitP={unitP}
              unitV={unitV}
            />
            <StatRow
              label={t(`${PREFIX}.EXISTING`, 'คนไข้เก่า')}
              patients={data.existingPatients}
              prevPatients={p.existingPatients}
              visits={data.existingVisits}
              prevVisits={p.existingVisits}
              unitP={unitP}
              unitV={unitV}
            />
          </Box>
        </Box>
      )}
    </WidgetCard>
  )
}
